import React from "react";
import PropTypes from "prop-types";

import useStrategyBenchmarkChartFrameMessaging from "../../../../../hooks/useStrategyBenchmarkChartFrameMessaging";
import { H5 } from "../../Typography";
import StrategyBenchmarkChartComparisonPicker from "./StrategyBenchmarkChartComparisonPicker";
import StrategyBenchmarkChartFrame from "./StrategyBenchmarkChartFrame";
import StrategyBenchmarkChartLegendCards from "./StrategyBenchmarkChartLegendCards";
import StrategyBenchmarkChartMarketPicker from "./StrategyBenchmarkChartMarketPicker";
import StrategyBenchmarkChartToolbar from "./StrategyBenchmarkChartToolbar";

const INLINE_CHART_HEIGHT = 210;
const FULLSCREEN_CHART_HEIGHT = 480;

const StrategyBenchmarkChartView = ({
  locale,
  translate,
  chartTitle,
  strategyLabelKey,
  chartSeries,
  comparisonType,
  comparisonOptions,
  rates,
  onComparisonChange,
  selectedMarket,
  marketOptions,
  onMarketChange,
  timeframe,
  timeframeOptions,
  onTimeframeChange,
  onFullscreenClick,
  fullscreenLabel,
  isRefetching = false,
  isFullscreen = false
}) => {
  const { frameRef, onFrameLoad } = useStrategyBenchmarkChartFrameMessaging({
    chartSeries,
    locale
  });
  const showMarketPicker =
    Array.isArray(marketOptions) &&
    marketOptions.length > 0 &&
    selectedMarket &&
    onMarketChange;

  return (
    <div className="strategy-benchmark-chart__card">
      <div className="strategy-benchmark-chart__header">
        <H5 tag="h2">{chartTitle}</H5>
        <div className="strategy-benchmark-chart__header-controls">
          {showMarketPicker ? (
            <StrategyBenchmarkChartMarketPicker
              locale={locale}
              translate={translate}
              selectedMarket={selectedMarket}
              marketOptions={marketOptions}
              onMarketChange={onMarketChange}
            />
          ) : null}
          <StrategyBenchmarkChartComparisonPicker
            locale={locale}
            translate={translate}
            strategyLabelKey={strategyLabelKey}
            comparisonType={comparisonType}
            comparisonOptions={comparisonOptions}
            rates={rates}
            onComparisonChange={onComparisonChange}
          />
        </div>
      </div>
      <StrategyBenchmarkChartLegendCards
        locale={locale}
        translate={translate}
        strategyLabelKey={strategyLabelKey}
        comparisonType={comparisonType}
        comparisonOptions={comparisonOptions}
        rates={rates}
      />
      <StrategyBenchmarkChartFrame
        frameRef={frameRef}
        height={isFullscreen ? FULLSCREEN_CHART_HEIGHT : INLINE_CHART_HEIGHT}
        onFrameLoad={onFrameLoad}
      />
      <div className="strategy-benchmark-chart__footer">
        <StrategyBenchmarkChartToolbar
          timeframe={timeframe}
          timeframeOptions={timeframeOptions}
          onTimeframeChange={onTimeframeChange}
          onFullscreenClick={onFullscreenClick}
          fullscreenLabel={fullscreenLabel}
          isRefetching={isRefetching}
        />
      </div>
    </div>
  );
};

StrategyBenchmarkChartView.propTypes = {
  locale: PropTypes.string.isRequired,
  translate: PropTypes.func.isRequired,
  chartTitle: PropTypes.string.isRequired,
  strategyLabelKey: PropTypes.string.isRequired,
  chartSeries: PropTypes.arrayOf(PropTypes.object).isRequired,
  comparisonType: PropTypes.string.isRequired,
  comparisonOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  rates: PropTypes.object.isRequired,
  onComparisonChange: PropTypes.func.isRequired,
  selectedMarket: PropTypes.string,
  marketOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      rateOfReturn: PropTypes.number,
      isLoading: PropTypes.bool
    })
  ),
  onMarketChange: PropTypes.func,
  timeframe: PropTypes.string.isRequired,
  timeframeOptions: PropTypes.array.isRequired,
  onTimeframeChange: PropTypes.func.isRequired,
  onFullscreenClick: PropTypes.func.isRequired,
  fullscreenLabel: PropTypes.string.isRequired,
  isRefetching: PropTypes.bool,
  isFullscreen: PropTypes.bool
};

export default StrategyBenchmarkChartView;
